import * as React from 'react';

import { useViewer } from '@/design';
import { readViewerChoice, writeViewerChoice } from '@/design/viewer-store';
import type { MyTaskFlag, MyTaskType } from '../api/work-item-contracts';
import type { SignalSpec } from './state-cards';

/** A My tasks signal: one that names a `flag` narrows the list to the tasks raising it. */
export type MyTaskSignalSpec = SignalSpec & { flag?: MyTaskFlag };

const OVERDUE: MyTaskSignalSpec = { key: 'overdue', label: 'overdue', tone: 'danger', flag: 'overdue' };
const DUE_TODAY: MyTaskSignalSpec = {
  key: 'due_today',
  label: 'due today',
  tone: 'attention',
  flag: 'due_today'
};
const NEW: MyTaskSignalSpec = { key: 'new', label: 'new', tone: 'info', flag: 'new' };

/**
 * My tasks' signals per type of task (SR-456) — the words and tones of the live counts My tasks'
 * cards show beside each type's count, in the order a compact card picks its first from. Each is a
 * flag survey-api narrows the list by; a type has only the signals it can raise — a reinstatement
 * or a metrics update has no due day, so neither is overdue or due today. Due today is a work
 * item's due day or a DD call booked today; new, assigned since the viewer's last visit.
 */
export const MY_TASK_SIGNALS: Partial<Record<MyTaskType, MyTaskSignalSpec[]>> = {
  compliance: [OVERDUE, DUE_TODAY, NEW],
  // A check has no due day: new is its one signal (PFR §4.4).
  identity_check: [NEW],
  // A meeting is due on the day it is booked for (SR-501).
  meeting: [OVERDUE, DUE_TODAY, NEW],
  dd_call: [
    OVERDUE,
    DUE_TODAY,
    { key: 'calls_this_week', label: 'calls this week', tone: 'info', flag: 'calls_this_week' },
    { key: 'no_outreach', label: 'no outreach', tone: 'attention', flag: 'no_outreach' },
    NEW
  ],
  reinstatement: [
    { key: 'edited_this_cycle', label: 'edited this cycle', tone: 'success', flag: 'edited_this_cycle' },
    { key: 'no_outreach', label: 'no outreach', tone: 'attention', flag: 'no_outreach' },
    NEW
  ],
  metrics: [
    { key: 'not_asked', label: 'not asked yet', tone: 'attention', flag: 'not_asked' },
    { key: 'asked_no_reply', label: 'asked, no reply', tone: 'info', flag: 'asked_no_reply' },
    NEW
  ]
};

/** All's signals, each summed over the types: the overdue, due today and new tasks. */
export const ALL_TASK_SIGNALS: readonly MyTaskSignalSpec[] = [OVERDUE, DUE_TODAY, NEW];

/** A load within this long of the last read is the same visit, its window kept. */
const SAME_VISIT_MS = 30 * 60 * 1000;

/**
 * The viewer's last visit to My tasks on this browser, under `veritant.myTasks.lastVisit.<viewer>`
 * as `{ since, at }`: `at` the last successful read, `since` the window that read showed. A load
 * within `SAME_VISIT_MS` of `at` is the same visit and shows the same window again, so a refresh
 * does not empty it; a later load is a new visit, whose window starts at `at`. A first visit has
 * no window, and so nothing new. Written only after a read of My tasks succeeds (`visited`), so a
 * failed read leaves the next load the window it would have had; the compliance queues read the
 * window and never write it, so "new" there is new since the last visit to My tasks.
 */
export function useLastVisit(): { since: Nullable<string>; visited: () => void } {
  return useVisitSince('veritant.myTasks.lastVisit');
}

/** `useLastVisit`'s rule under another page's key, `<keyBase>.<viewer>`. */
export function useVisitSince(keyBase: string): { since: Nullable<string>; visited: () => void } {
  const viewer = useViewer();
  const key = viewer ? `${keyBase}.${viewer}` : null;
  const since = React.useMemo(() => {
    const stored = readViewerChoice(key);
    const at = typeof stored.at === 'string' && !Number.isNaN(Date.parse(stored.at)) ? stored.at : null;
    if (at === null) return null;
    if (Date.now() - Date.parse(at) >= SAME_VISIT_MS) return at;
    return typeof stored.since === 'string' ? stored.since : null;
  }, [key]);
  const visited = React.useCallback(
    () => writeViewerChoice(key, { since, at: new Date().toISOString() }),
    [key, since]
  );
  return { since, visited };
}
